import { useEffect } from 'react';
import {
    EnvelopeIcon,
    ExclamationCircleIcon,
    PhoneIcon,
} from '@heroicons/react/20/solid';

import { uniqueIdTransfer } from '@/apiCalls/uniqueIDTransferHelper';
import { Button, EButtonType } from '@/components/Button';
import { ContactPanel } from '@/components/ContactPanel';
import { DisplayComponent } from '@/components/DisplayComponent';
import { EEventType } from '@/components/eventType';
import { Header } from '@/components/Header';
import { OutcomeCard } from '@/components/OutcomeCard';
import { RemoteImage } from '@/components/RemoteImage';
import { EDisplays, selectPartnerNumber, setDisplay } from '@/context/display';
import { useAppDispatch, useAppSelector } from '@/context/storeHooks';
import { useCopy } from '@/copy';
import { EMAIL, PHONE } from '@/utils/constants';

export const testId = 'IndicativelyDeclined';

export function IndicativelyDeclined(): JSX.Element {
    const { outcomes, declinedCard, declinedDetail } = useCopy();
    const outcome = outcomes.declined;
    const dispatch = useAppDispatch();
    const partnerPhone = useAppSelector(selectPartnerNumber) || PHONE;

    function handleBackToStart(): void {
        dispatch(setDisplay(EDisplays.QUICK_CALCULATOR));
    }
    const baseId = 'screen-indicativelyDeclined';

    // The original layout renders without a DisplayComponent, so no page view
    // fired for it at all — a decline was the one terminal state with no screen
    // recorded. Emitted directly rather than by wrapping in DisplayComponent,
    // which would change the layout of a live screen for a tracking reason.
    //
    // The card layout does use DisplayComponent, which reports the same
    // `${baseId}-screen` view itself. Firing here as well would count every
    // CarExpert decline twice.
    useEffect(() => {
        if (declinedCard) return;
        void uniqueIdTransfer({
            eventType: EEventType.VISIBLE_ON_SCREEN,
            applicationData: { id: `${baseId}-screen` },
        });
    }, [declinedCard]);

    if (declinedCard) {
        return (
            <div data-testid={testId} id={`${baseId}-screen`}>
                <DisplayComponent baseId={baseId}>
                    <Header />
                    <OutcomeCard
                        icon={<ExclamationCircleIcon className='h-6 w-6' />}
                        tint='bg-optionTrack text-panel'
                        title={outcome.title}
                        lines={outcome.body}
                    />

                    {/* The number is partner config, not copy. */}
                    <ContactPanel
                        baseId={baseId}
                        text={declinedDetail.contactText}
                        cta={declinedDetail.callCta}
                        phone={partnerPhone}
                    />
                    {/* These screens end the journey. Without this the only
                        way out is closing the widget. */}
                    <div className='_flex pt-3 pb-2'>
                        <Button
                            id={`${baseId}-button-backToStart`}
                            onClick={handleBackToStart}
                            type={EButtonType.SECONDARY_OUTLINE}
                        >
                            <p className='text-sm'>{declinedDetail.backToStart}</p>
                        </Button>
                    </div>
                </DisplayComponent>
            </div>
        );
    }

    return (
        <div data-testid={testId} id={`${baseId}-screen`}>
            <Header />
            <div className='flex-center pt-10 pb-4 relative'>
                <div className='bg-primary h-40 w-40 rounded-full' />
                <RemoteImage fileName='avatar.svg' className='absolute' />
            </div>
            <div className='text-center _flex flex-col space-y-2'>
                <h3 className='uppercase text-xl text-primary font-bold '>
                    {outcome.title}
                </h3>
                {outcome.body.length > 1 ? (
                    outcome.body.map((line, i) => (
                        <p
                            key={line}
                            className={
                                i === 0 ? 'text-secondary pb-4' : 'text-sm'
                            }
                        >
                            {line}
                        </p>
                    ))
                ) : (
                    <>
                        <p className='text-secondary pb-4'>
                            Unfortunately, we can&apos;t help you right now.
                        </p>
                        <p className='font-bold'>
                            But, we&apos;re here to help
                        </p>
                        <p className='text-sm'>
                            Although we can&apos;t help you right now, do feel{' '}
                            <br />
                            free to contact us for more information and <br />
                            how we can help you in the future.
                        </p>
                    </>
                )}
            </div>

            <div className='flex-center pt-4'>
                <div className='border-lg border-2 text-sm bg-white py-2 rounded-xl px-10 text-gray-500 text-center'>
                    <h3 className='font-bold pb-4'>
                        Have a question? <br />
                        Get in touch:
                    </h3>
                    <a href={`tel:${PHONE}`} className='flex-center space-x-2'>
                        <PhoneIcon className='h-4 w-4' />
                        <p>{PHONE}</p>
                    </a>
                    <a
                        href={`mailto:${EMAIL}`}
                        className='flex-center space-x-2'
                    >
                        <EnvelopeIcon className='h-4 w-4' />
                        <p>{EMAIL}</p>
                    </a>
                </div>
            </div>
        </div>
    );
}
